import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Mail, Phone, Share2, Check, Linkedin, Instagram, MapPin, Github } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { captureCrmLead } from "../lib/crm";
import { Logo } from "./Logo";
import {
  LEGAL_ENTITY, REGISTERED_ADDRESS,
  PRIMARY_EMAIL, PRIMARY_PHONE, SOCIAL_LINKS
} from "../data";

const LEGAL = [
  { label: "Privacy Policy", to: "/legal/privacy" },
  { label: "Terms & Conditions", to: "/legal/terms" },
  { label: "Refund Policy", to: "/legal/refund" },
  { label: "Cookie Policy", to: "/legal/cookie" },
  { label: "Disclaimer", to: "/legal/disclaimer" },
  { label: "Cancellation Policy", to: "/legal/cancellation" },
  { label: "Sitemap", to: "/sitemap" },
];

const OFFERINGS = [
  { label: "Software Development", to: "/services/software-development" },
  { label: "AI Solutions", to: "/services/ai-solutions" },
  { label: "IoT & Automation", to: "/services/iot-solutions" },
  { label: "Cybersecurity", to: "/guardian" },
  { label: "Cloud Infrastructure", to: "/services/cloud-infrastructure" },
  { label: "Technology Consultancy", to: "/services/consultancy" },
  { label: "nORBIT Enterprise OS", to: "/norbit" },
  { label: "Reach Growth Engine", to: "/reach" },
];

const COMPANY = [
  { label: "About", to: "/about" },
  { label: "Services", to: "/services" },
  { label: "Industries", to: "/industries" },
  { label: "Our Work", to: "/portfolio" },
  { label: "Blog", to: "/blog" },
  { label: "Careers", to: "/careers" },
  { label: "Contact", to: "/contact" },
];

export const Footer = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [settings, setSettings] = useState({
    registered_address: REGISTERED_ADDRESS,
    legal_entity: LEGAL_ENTITY,
    primary_email: PRIMARY_EMAIL,
    primary_phone: PRIMARY_PHONE,
  });

  useEffect(() => {
    api.get("/settings")
      .then((res) => {
        if (res.data) setSettings((prev) => ({ ...prev, ...res.data }));
      })
      .catch(() => {});
  }, []);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      // Direct CRM webhook capture
      captureCrmLead({
        name: "Newsletter Subscriber",
        email: email.trim().toLowerCase(),
        service: "Newsletter Subscription",
        notes: "Subscribed via website footer newsletter form",
      });

      const { data } = await api.post("/newsletter", { email });
      toast.success(data.status === "already_subscribed" ? "You're already subscribed." : "Subscribed. Welcome aboard!");
      setEmail("");
    } catch { toast.error("Could not subscribe. Try again."); }
    finally { setLoading(false); }
  };

  const handleShare = async () => {
    const shareUrl = "https://neuravolt.in";
    if (navigator.clipboard) {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success("Shareable website link copied to clipboard!");
      setTimeout(() => setCopied(false), 2500);
    } else {
      toast.info("Website link: https://neuravolt.in");
    }
  };

  return (
    <footer data-testid="footer" className="section-dark relative pt-10 sm:pt-12 pb-8 sm:pb-10 px-5 sm:px-8 overflow-hidden border-t border-white/10">
      {/* Background Image Layer (inspired by americanchase.com) */}
      <div className="absolute inset-0 z-0 pointer-events-none overflow-hidden">
        <img
          src="/brand/footer-team-bg.jpg"
          alt=""
          className="w-full h-full object-cover object-center scale-105 opacity-40 sm:opacity-45 filter contrast-115 brightness-90"
        />
        {/* Ambient Dark Tech Gradients & Readability Overlays */}
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B0B0B] via-[#0B0B0B]/60 to-[#0B0B0B]/75" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_60%_at_50%_0%,rgba(255,90,31,0.12),transparent_70%)]" />
      </div>

      <div className="blob w-[420px] h-[420px] bg-[#FF5A1F] -bottom-40 -left-20 pointer-events-none" style={{ opacity: 0.18 }} />
      <div className="max-w-7xl mx-auto relative z-10">
        {/* Tier 1: Brand & Contacts on Left (5 Cols), Spacious Navigation on Right (7 Cols) */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 pb-8 border-b border-white/[0.08]">
          {/* Col 1: Logo, Bio, Direct Contacts, Newsletter & Socials */}
          <div className="lg:col-span-5 space-y-4">
            <div>
              <div className="mb-3">
                <Logo dark={true} />
              </div>

              <p className="text-neutral-400 max-w-md leading-relaxed text-xs sm:text-sm">
                AI first software engineering: custom software, AI, mobile, cloud and enterprise solutions for ambitious businesses worldwide.
              </p>
            </div>

            {/* Direct Work Email & Phone Contacts */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-2.5 sm:gap-5 text-xs text-neutral-300">
              <div className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-[#FF5A1F] shrink-0" />
                <span className="text-neutral-500">Email:</span>
                <a
                  href={`mailto:${settings.primary_email || PRIMARY_EMAIL}`}
                  className="text-white hover:text-[#FF5A1F] font-medium transition-colors truncate"
                >
                  {settings.primary_email || PRIMARY_EMAIL}
                </a>
              </div>
              <div className="flex items-center gap-2">
                <Phone className="w-4 h-4 text-[#FF5A1F] shrink-0" />
                <span className="text-neutral-500">Phone:</span>
                <a
                  href={`tel:${(settings.primary_phone || PRIMARY_PHONE).replace(/\s+/g, "")}`}
                  className="text-white hover:text-[#FF5A1F] font-medium transition-colors whitespace-nowrap"
                >
                  {settings.primary_phone || PRIMARY_PHONE}
                </a>
              </div>
            </div>

            {/* Newsletter Subscription */}
            <div className="pt-0.5">
              <form onSubmit={subscribe} className="w-full max-w-md" data-testid="newsletter-form">
                <div className="flex items-center rounded-full bg-white/[0.06] border border-white/10 focus-within:border-[#FF5A1F] p-1.5 transition-all shadow-inner">
                  <input
                    data-testid="newsletter-input"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email to subscribe..."
                    className="w-full min-w-0 bg-transparent pl-4 pr-2 py-1 text-xs sm:text-sm text-white outline-none placeholder:text-neutral-500"
                  />
                  <button
                    data-testid="newsletter-submit"
                    type="submit"
                    disabled={loading}
                    className="shrink-0 px-4 py-1.5 rounded-full bg-[#FF5A1F] text-white text-xs sm:text-sm font-semibold hover:bg-[#ff6d38] transition-colors disabled:opacity-60 shadow-md shadow-[#FF5A1F]/30 cursor-pointer"
                  >
                    {loading ? "..." : "Join"}
                  </button>
                </div>
              </form>
            </div>

            {/* Social Links & Share Website */}
            <div className="flex flex-wrap items-center gap-2 pt-0.5">
              <a
                href="https://www.linkedin.com/company/neuravolt-technology/posts/?feedView=all"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Neuravolt LinkedIn Company Page"
                title="LinkedIn"
                className="w-8.5 h-8.5 rounded-xl bg-white/[0.05] border border-white/10 hover:bg-[#0A66C2] hover:border-[#0A66C2] flex items-center justify-center transition-all duration-200 group shrink-0 shadow-xs"
              >
                <Linkedin className="w-4 h-4 text-neutral-400 group-hover:text-white transition-colors" />
              </a>
              <a
                href={SOCIAL_LINKS.instagram || "https://instagram.com/we.neuravolt"}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Neuravolt Instagram"
                title="Instagram"
                className="w-8.5 h-8.5 rounded-xl bg-white/[0.05] border border-white/10 hover:bg-gradient-to-br hover:from-[#833ab4] hover:via-[#fd1d1d] hover:to-[#fcb045] hover:border-transparent flex items-center justify-center transition-all duration-200 group shrink-0 shadow-xs"
              >
                <Instagram className="w-4 h-4 text-neutral-400 group-hover:text-white transition-colors" />
              </a>
              <a
                href={SOCIAL_LINKS.github || "https://github.com/neuravolt-technologies"}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Neuravolt GitHub"
                title="GitHub"
                className="w-8.5 h-8.5 rounded-xl bg-white/[0.05] border border-white/10 hover:bg-white/20 hover:border-white/30 flex items-center justify-center transition-all duration-200 group shrink-0 shadow-xs"
              >
                <Github className="w-4 h-4 text-neutral-400 group-hover:text-white transition-colors" />
              </a>
              <a
                href={SOCIAL_LINKS.google_maps || "https://www.google.com/maps/place/Neuravolt+Technology/data=!4m2!3m1!1s0x0:0x47b13dd0dbd4c8f5"}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Neuravolt Google Maps Profile & Reviews"
                title="Google Maps"
                className="w-8.5 h-8.5 rounded-xl bg-white/[0.05] border border-white/10 hover:bg-[#FF5A1F] hover:border-[#FF5A1F] flex items-center justify-center transition-all duration-200 group shrink-0 shadow-xs"
              >
                <MapPin className="w-4 h-4 text-neutral-400 group-hover:text-white transition-colors" />
              </a>
              <button
                type="button"
                onClick={handleShare}
                aria-label="Share website link"
                title="Copy shareable website link"
                className="px-3 py-1.5 rounded-xl bg-white/[0.05] border border-white/10 hover:border-[#FF5A1F] text-xs text-neutral-300 hover:text-white flex items-center gap-1.5 transition-all shrink-0 cursor-pointer shadow-xs"
              >
                {copied ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Share2 className="w-3.5 h-3.5 text-[#FF5A1F]" />}
                <span>{copied ? "Copied!" : "Share"}</span>
              </button>
            </div>
          </div>

          {/* Right: Generous 3 Navigation Columns (7 Columns) */}
          <div className="lg:col-span-7 grid grid-cols-2 sm:grid-cols-3 gap-6 lg:gap-8 lg:pl-6">
            {/* Services & Products */}
            <div>
              <h4 className="font-heading font-bold text-xs uppercase tracking-widest text-neutral-300 mb-3 pb-1 border-b border-white/[0.08] inline-block">
                Services & Products
              </h4>
              <ul className="space-y-2 text-xs sm:text-sm text-neutral-400">
                {OFFERINGS.map((x) => (
                  <li key={x.label}>
                    {x.external || x.to?.startsWith("http") ? (
                      <a href={x.to} target="_blank" rel="noopener noreferrer" className="hover:text-[#FF5A1F] hover:translate-x-0.5 transition-all inline-flex items-center gap-1">
                        <span>{x.label}</span>
                      </a>
                    ) : (
                      <Link to={x.to} className="hover:text-[#FF5A1F] hover:translate-x-0.5 transition-all inline-block">
                        {x.label}
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>

            {/* Company */}
            <div>
              <h4 className="font-heading font-bold text-xs uppercase tracking-widest text-neutral-300 mb-3 pb-1 border-b border-white/[0.08] inline-block">
                Company
              </h4>
              <ul className="space-y-2 text-xs sm:text-sm text-neutral-400">
                {COMPANY.map((x) => (
                  <li key={x.label}>
                    <Link to={x.to} className="hover:text-[#FF5A1F] hover:translate-x-0.5 transition-all inline-block">
                      {x.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Legal */}
            <div>
              <h4 className="font-heading font-bold text-xs uppercase tracking-widest text-neutral-300 mb-3 pb-1 border-b border-white/[0.08] inline-block">
                Legal
              </h4>
              <ul className="space-y-2 text-xs sm:text-sm text-neutral-400">
                {LEGAL.map((x) => (
                  <li key={x.label}>
                    <Link to={x.to} className="hover:text-[#FF5A1F] hover:translate-x-0.5 transition-all inline-block">
                      {x.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* Tier 2: Registered Office Address */}
        <div className="pt-6 pb-6 flex flex-col items-center justify-center text-center">
          <div className="flex items-center justify-center gap-2 mb-3.5">
            <MapPin className="w-4 h-4 text-[#FF5A1F]" />
            <h4 className="font-heading font-bold text-xs uppercase tracking-widest text-neutral-300">
              Registered Office
            </h4>
          </div>

          <div className="w-fit max-w-full mx-auto rounded-xl py-2 sm:py-2.5 px-4 sm:px-5 bg-white/[0.04] border border-white/[0.08] hover:border-white/20 transition-all shadow-xs backdrop-blur-md flex items-center justify-center text-center">
            <p className="text-neutral-400 text-xs leading-relaxed text-center">{settings.registered_address || REGISTERED_ADDRESS}</p>
          </div>
        </div>

        {/* Tier 3: Bottom Bar (Copyright, Recognition Logos in Between, & Attribution) */}
        <div className="pt-4 border-t border-white/[0.08] flex flex-col md:flex-row justify-between items-center gap-4 text-xs text-neutral-500">
          <p className="text-center md:text-left whitespace-nowrap">© 2026 Neuravolt. All Rights Reserved.</p>

          {/* Official Recognition Logos (Startup India & MSME) placed in between */}
          <div className="flex flex-wrap items-center justify-center gap-3 sm:gap-4">
            <div
              title="Startup India — Recognized by DPIIT, Government of India"
              className="h-11 sm:h-12 px-4 py-1.5 rounded-xl bg-white border border-white/20 shadow-md flex items-center justify-center select-none"
            >
              <img
                src="/brand/startup-india-logo.png"
                alt="Startup India"
                className="h-7 sm:h-8 w-auto object-contain"
              />
            </div>

            <div
              title="Ministry of Micro, Small and Medium Enterprises (MSME) — Government of India"
              className="h-11 sm:h-12 px-4 py-1.5 rounded-xl bg-white border border-white/20 shadow-md flex items-center justify-center select-none"
            >
              <img
                src="/brand/msme-official-logo.png"
                alt="Ministry of Micro, Small and Medium Enterprises (MSME) — Government of India"
                className="h-8 sm:h-9 w-auto object-contain"
              />
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-center gap-4">
            <p className="whitespace-nowrap">Powered by <span className="text-neutral-300 font-medium">{settings.legal_entity || LEGAL_ENTITY}</span></p>
            <div className="flex items-center gap-3">
              <a href="https://www.linkedin.com/company/neuravolt-technology/posts/?feedView=all" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" title="LinkedIn" className="hover:text-[#0A66C2] transition-colors">
                <Linkedin className="w-3.5 h-3.5" />
              </a>
              <a href={SOCIAL_LINKS.instagram || "https://instagram.com/we.neuravolt"} target="_blank" rel="noopener noreferrer" aria-label="Instagram" title="Instagram" className="hover:text-[#E1306C] transition-colors">
                <Instagram className="w-3.5 h-3.5" />
              </a>
              <a href={SOCIAL_LINKS.github || "https://github.com/neuravolt-technologies"} target="_blank" rel="noopener noreferrer" aria-label="GitHub" title="GitHub" className="hover:text-white transition-colors">
                <Github className="w-3.5 h-3.5" />
              </a>
              <a href={SOCIAL_LINKS.google_maps || "https://www.google.com/maps/place/Neuravolt+Technology/data=!4m2!3m1!1s0x0:0x47b13dd0dbd4c8f5"} target="_blank" rel="noopener noreferrer" aria-label="Google Maps" title="Google Maps" className="hover:text-[#FF5A1F] transition-colors">
                <MapPin className="w-3.5 h-3.5" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
